import axios from 'axios';

/**
 * Service for fetching vital signs data
 */

/**
 * Fetch the latest vital signs for a patient
 * @param {string} patientId - The patient ID
 * @returns {Promise} - Promise resolving to the latest vitals data
 */
export const getLatestVitals = async (patientId) => {
  try {
    const response = await axios.get(`/patients/${patientId}/latest-vitals-trend`);
    return response.data;
  } catch (error) {
    console.error('Error fetching latest vitals:', error);
    throw error;
  }
};

/**
 * Extract specific vital signs for the banner display
 * @param {Object} vitalsData - Raw vitals data from API
 * @returns {Object} - Formatted vitals data for banner display
 */
export const formatVitalsForBanner = (vitalsData) => {
  if (!vitalsData || !vitalsData.data || !vitalsData.data.vitals) {
    return {
      bloodPressure: null,
      heartRate: null,
      weight: null,
      bmi: null,
      temperature: null
    };
  }

  const vitals = vitalsData.data.vitals;

  // Helper function to extract value and timestamp from vital entry
  // The API returns data in format: { mostRecent: { value, timestamp, source, units }, priorRecent: {...} }
  const extractVitalInfo = (vitalEntry) => {
    if (!vitalEntry || !vitalEntry.mostRecent) return null;

    const mostRecent = vitalEntry.mostRecent;
    return {
      value: mostRecent.value,
      timestamp: mostRecent.timestamp,
      units: mostRecent.units || ''
    };
  };

  // Extract blood pressure (combine systolic and diastolic)
  const systolic = extractVitalInfo(vitals.vitals_sbp);
  const diastolic = extractVitalInfo(vitals.vitals_dbp);

  let bloodPressure = null;
  if (systolic && diastolic) {
    bloodPressure = {
      value: `${systolic.value}/${diastolic.value}`,
      timestamp: systolic.timestamp, // Use systolic timestamp
      units: 'mmHg'
    };
  } else if (systolic) {
    bloodPressure = {
      value: `${systolic.value}/-`,
      timestamp: systolic.timestamp,
      units: 'mmHg'
    };
  } else if (diastolic) {
    bloodPressure = {
      value: `-/${diastolic.value}`,
      timestamp: diastolic.timestamp,
      units: 'mmHg'
    };
  }

  return {
    bloodPressure,
    heartRate: extractVitalInfo(vitals.vitals_rhr),
    weight: extractVitalInfo(vitals.vitals_weight),
    bmi: extractVitalInfo(vitals.vitals_bmi),
    temperature: extractVitalInfo(vitals.vitals_temp_c)
  };
};
